import { useEffect, useRef } from 'react';
import { StageReport, stageActivityFailed, stageActivityStatus, stageOfReport } from '../utils/stageActivity';

// Method-shaped so a step still typed for bare stages can pass its callback.
type Reporter = { report(stage: StageReport | null): void }['report'];

/**
 * Forwards a step's own working flag up to the wizard, which is what decides
 * whether Robbie still has a next best step to offer. The step keeps the
 * single copy of the flag — nothing is mirrored here — and clearing on
 * unmount means a step can never leave the panel claiming work that stopped.
 * `text` is the overlay's own wording for the wait, so the panel repeats it;
 * a report that already carries a live line keeps it.
 */
export const useReportStageActivity = (
  report: Reporter | undefined,
  stage: StageReport | null,
  text?: string,
): void => {
  const effective: StageReport | null =
    stage && text && typeof stage === 'string' ? { stage, text } : stage;
  // Keyed on the stage and its line, not object identity, so a live line
  // rebuilt every render does not re-report on every render.
  const latest = useRef(effective);
  latest.current = effective;
  const kind = stageOfReport(effective);
  const status = stageActivityStatus(effective);
  const failed = stageActivityFailed(effective);
  useEffect(() => {
    report?.(latest.current);
    return () => report?.(null);
  }, [report, kind, status, failed]);
};

export default useReportStageActivity;
